import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import {
    CheckboxVisibility, CommandBarButton, ConstrainMode, DirectionalHint, ICommandBarItemProps, IconButton, IContextualMenuItem,
    IDialogContentProps, IObjectWithKey, MessageBar, MessageBarType, ScrollablePane, ScrollbarVisibility, Selection
} from 'office-ui-fabric-react';
import { Dictionary, EntityType, updateCheckboxOptions } from '../../../entities/common';
import { ApplicationState } from '../../../store';
import { UserState, isAdminViewer, isInReadonlyMode } from "../../../store/User";
import { canCreateResource } from "../../../store/permissions";
import Spinner from '../../common/Spinner';
import * as Metadata from "../../../entities/Metadata";
import EntityDetailsList, { onRenderDetailsHeader } from '../../common/extensibleEntity/EntityDetailsList';
import RemoveDialog from '../../common/RemoveDialog';
import { IDeletionResult } from '../../../store/services/storeHelper';
import { groupBy, notUndefined } from '../../utils/common';
import * as FiltersStore from "../../../store/filters";
import { FilterHelper } from '../../../store/calendar/filters';
import EntityGroupHeader, { EntityGroup } from '../../common/extensibleEntity/EntityGroupHeader';
import { nameof, namesof } from '../../../store/services/metadataService';
import SelectionModeSwitchableCommandBar from '../../common/SelectionModeSwitchableCommandBar';
import { ClearFilterComponent } from '../../common/sectionsControl/SectionPlaceholder';
import ListMenuFilterSelector from '../../common/ListMenuFilterSelector';
import { TenantState } from '../../../store/Tenant';
import { actionCreators as calendarActionCreators } from '../../../store/CalendarsListStore';
import { Calendar, CalendarStatus, canCalendarBeDeleted, ICalendarAttrs, getUserCalendar } from '../../../store/calendar/common';
import CalendarCreation from '../../calendar/CalendarCreation';
import { ActiveFilter as ProjectActiveFilter } from '../../../store/project/filters';
import { ActiveFilter as ResourceActiveFilter } from '../../../store/resource/filters';
import * as ResourcesListStore from '../../../store/ResourcesListStore';

const fakeFields: Metadata.Field[] = [
    {
        id: "94f73E1f-0934-4319-ac6d-becb09b3072d",
        name: nameof<Calendar>("isDefault"),
        label: "Is Default",
        type: Metadata.FieldType.Flag,
        isFake: true,
        isNative: false,
        isReadonly: true,
        isCustom: false,
        isSystem: false,
        group: Metadata.FieldGroup.SystemFields,
        settings: {
            views: {
                list: {
                    componentPath: "calendar/IsDefault",
                    maxWidth: 150
                }
            }
        },
    }
];

const GLOBAL_CALENDARS_GROUP_ID = "global";
const USER_CALENDAR_GROUP_ID = "user";

export const CalendarGroups: Metadata.Group[] = [
    {
        id: GLOBAL_CALENDARS_GROUP_ID,
        name: "Calendars",
        color: "#128a10",
        isShown: true,
        isDefault: true
    },
    {
        id: USER_CALENDAR_GROUP_ID,
        name: "My Calendar",
        color: "#128a10",
        isShown: true,
        isDefault: false
    },
];

type StateProps = {
    fields: Metadata.Field[];
    filters: Metadata.IFilter<Metadata.BaseFilterValue>[];
    user: UserState;
    tenant: TenantState;
    calendars: Calendar[];
    isLoading: boolean;
    deletionResult?: IDeletionResult[];
};

type ActionProps = {
    calendarsActions: typeof calendarActionCreators;
    resourcesActions: typeof ResourcesListStore.actionCreators;
};

export type Props = StateProps & ActionProps & RouteComponentProps<{}>;

const CalendarsList = (props: Props) => {
    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
    const selected = React.useMemo(
        () => props.calendars.filter(_ => selectedIds.includes(_.id)),
        [props.calendars, selectedIds]);
    const [addNewCalendar, setAddNewCalendar] = React.useState<boolean>(false);
    const [toRemove, setToRemove] = React.useState<Calendar[]>([]);
    const [activeFilter, setActiveFilter] = React.useState(() => Metadata.Filter.getAutoFilter(props.filters));

    const canManage = !isInReadonlyMode(props.user, props.tenant) && canCreateResource(props.user.permissions.resource);

    React.useEffect(() => {
        if (props.user.resource?.id) {
            props.resourcesActions.loadResource(props.user.resource?.id, true);
        }
    }, []);

    const getCalendarGroupId = (calendar: Calendar): string =>
        calendar.isUserCalendar ? USER_CALENDAR_GROUP_ID : GLOBAL_CALENDARS_GROUP_ID;

    const selection = React.useMemo(() => new Selection({
        getKey: (_: IObjectWithKey) => getKey(_ as Calendar),
        canSelectItem: (_: IObjectWithKey) => !(_ as Calendar).isUserCalendar,
        onSelectionChanged: () => {
            setSelectedIds((selection.getSelection() as Calendar[]).map(_ => _.id));
        }
    }), []);

    const entityFilterHelper = React.useMemo(() => new FilterHelper({ fields: props.fields }), [props.fields]);

    const applyFilter = (calendars: Calendar[]) => calendars.filter(isItemVisible);

    const isItemVisible = (calendar: Calendar): boolean => {
        if (!activeFilter) {
            return true;
        }

        const filterValue = activeFilter.value;
        const allAttributes = entityFilterHelper.getFilterAttributes(props.fields);

        for (const type in filterValue) {
            if (!entityFilterHelper.helpersMap[type].validateItem(calendar, filterValue[type], allAttributes.filter(_ => _.type === type))) {
                return false;
            }
        }

        return true;
    };

    const updateStatus = (calendars: Calendar[], status: CalendarStatus) => {
        const statusUpdates = calendars.reduce((acc, calendar) => {
            acc[calendar.id] = { [nameof<ICalendarAttrs>('Status')]: status };
            return acc;
        }, {} as Dictionary<Dictionary<CalendarStatus>>);
        props.calendarsActions.bulkUpdateCalendars(statusUpdates)
    };

    const buildActivateMenuItem = (calendars: Calendar[]): IContextualMenuItem | undefined =>
        calendars.length === 1 && calendars[0].isUserCalendar || !calendars.every(_ => _.isEditable)
            ? undefined
            : buildStatusItemIfStatusDiffersAndNotDefault(calendars, CalendarStatus.Active, {
                key: "activate",
                name: "Activate",
                iconProps: { iconName: "CircleAddition" },
                onClick: () => updateStatus(calendars, CalendarStatus.Active),
            });

    const buildDeactivateMenuItem = (calendars: Calendar[]): IContextualMenuItem | undefined =>
        calendars.length === 1 && calendars[0].isUserCalendar || !calendars.every(_ => _.isEditable)
            ? undefined
            : buildStatusItemIfStatusDiffersAndNotDefault(calendars, CalendarStatus.Inactive, {
                key: "deactivate",
                name: "Deactivate",
                iconProps: { iconName: "Blocked2" },
                onClick: () => updateStatus(calendars, CalendarStatus.Inactive),
            });

    const buildViewRelatedProjectsMenuItem = (calendar: Calendar): IContextualMenuItem | undefined =>
        !calendar.isUserCalendar
            && (calendar.attributes.Status === CalendarStatus.Active || calendar.attributes.Status === CalendarStatus.Inactive)
            ? {
                key: "ViewRelatedProjects",
                name: "View Related Projects",
                iconProps: { iconName: "Blocked2" },
                disabled: calendar.projectIds.length === 0,
                onClick: () => { 
                    props.history.push(`/projects`, new ProjectActiveFilter("Custom").withCalendar(calendar.id).buildLocationState());
                }
            }
            : undefined;

    const buildViewRelatedResourcesMenuItem = (calendar: Calendar): IContextualMenuItem | undefined =>
        !calendar.isUserCalendar
            && (calendar.attributes.Status === CalendarStatus.Active || calendar.attributes.Status === CalendarStatus.Inactive)
            ? {
                key: "ViewRelatedResources",
                name: "View Related Resources",
                iconProps: { iconName: "Blocked2" },
                disabled: calendar.resourceIds.length === 0,
                onClick: () => { 
                    props.history.push(`/resources`, new ResourceActiveFilter("Custom").withCalendar(calendar.id).build());
                }
            }
            : undefined;

    const renderThreeDotsMenu = (calendar: Calendar) => {
        const canBeDeleted = canCalendarBeDeleted(calendar)
            && canManage;
        const deleteDisabled = props.isLoading;
        const items = [
            buildActivateMenuItem([calendar]),
            buildDeactivateMenuItem([calendar]),
            buildViewRelatedProjectsMenuItem(calendar),
            buildViewRelatedResourcesMenuItem(calendar),
            buildSetAsDefaultAction(calendar, props.calendarsActions.makeCalendarDefault),
            {
                key: 'edit',
                name: calendar.isEditable ? 'Edit' : 'View',
                iconProps: { iconName: calendar.isEditable ? 'Edit' : 'View' },
                onClick: () => props.history.push(`/calendar/${calendar.id}`)
            },
            calendar.isEditable && !calendar.isUserCalendar ? {
                key: 'clone',
                name: 'Clone',
                iconProps: { iconName: 'Copy' },
                onClick: () => props.calendarsActions.cloneCalendar(calendar.id)
            } : undefined,
            canBeDeleted ? {
                key: 'delete',
                name: 'Delete',
                iconProps: { iconName: "Delete", style: { color: deleteDisabled ? 'initial' : 'red' } },
                disabled: deleteDisabled,
                style: {
                    color: (deleteDisabled ? 'initial' : 'red'),
                    backgroundColor: (props.isLoading ? 'lightgrey' : 'unset')
                },
                onClick: () => setToRemove([calendar])
            } : undefined
        ].filter(notUndefined) as IContextualMenuItem[];

        return <div className="menu">
            <IconButton
                menuIconProps={{ iconName: 'PPMXMore' }}
                menuProps={{
                    directionalHint: DirectionalHint.bottomRightEdge,
                    items
                }}
            />
        </div>;
    }

    const buildSelectionModeCommands = (): ICommandBarItemProps[] => {
        if (!canManage) {
            return [];
        }

        return [
            buildActivateMenuItem(selected),
            buildDeactivateMenuItem(selected),
            selected.some(calendar => canCalendarBeDeleted(calendar))
                ? {
                    key: 'delete',
                    text: 'Delete',
                    iconProps: { iconName: 'Delete' },
                    onClick: () => setToRemove(selected)
                } : undefined
        ].filter(notUndefined);
    }

    const buildListGroups = () => {
        const byGroupId = groupBy(props.calendars, getCalendarGroupId);
        return CalendarGroups.filter(_ => _.id !== USER_CALENDAR_GROUP_ID || !isAdminViewer(props.user)).map<EntityGroup>(_ => ({
            key: _.id,
            name: _.name,
            color: _.color,
            count: byGroupId[_.id]?.length ?? 0,
            hideMarker: true
        }));
    };

    const buildDeletionResultDialogContent = (deletionResult: IDeletionResult[]): IDialogContentProps => {
        if (deletionResult.length === 1) {
            return deletionResult[0].isDeleted
                ? {
                    title: "Calendar deletion is complete",
                    subText: `Calendar "${deletionResult[0].name}" was deleted successfully.`
                }
                : {
                    title: "Unable to delete calendar",
                    subText: "The selected calendar cannot be deleted."
                }
        }

        const deleted = deletionResult.filter(_ => _.isDeleted);
        return {
            title: "Calendars deletion is completed",
            subText: `Selected calendars (${deleted.length} items) were deleted successfully.`
        }
    }

    const buildCommands = (): IContextualMenuItem[] => {
        return [
            {
                key: 'new-calendar',
                onRender: () => (
                    <CommandBarButton
                        key="new-entity-header-button"
                        className='align-center new-entity-header-button'
                        iconProps={{ iconName: 'Add' }}
                        text='New Calendar'
                        disabled={!canManage}
                        styles={{ root: { height: 40, padding: '0 8px' } }}
                        onClick={() => setAddNewCalendar(true)}
                    />
                )
            }
        ];
    }

    const buildFarCommands = (): IContextualMenuItem[] => {
        return [
            {
                key: 'filter',
                onRender: () => (
                    <ListMenuFilterSelector
                        filters={props.filters}
                        activeFilter={activeFilter}
                        canManageConfiguration={canManage}
                        onActiveItemChanged={(id) => {
                            const newActiveFilter = props.filters.find(_ => _.id === id);
                            setActiveFilter(newActiveFilter!);
                        }}
                    />
                )
            }
        ];
    }

    const isFieldFake = (field: Metadata.Field) => fakeFields.some(_ => _.id === field.id);

    const selectionModeCommands = buildSelectionModeCommands();

    if (props.isLoading) {
        return <Spinner />;
    }

    return <>
        <div className="processes">
            <h2>Calendar Management</h2>
            <div className="entities-list">
                <div className="entities-list-header">
                    <SelectionModeSwitchableCommandBar
                        items={buildCommands()}
                        farItems={buildFarCommands()}
                        selectionMode={{
                            enabled: selected.length > 0,
                            items: selectionModeCommands,
                            selectedCount: selected.length,
                            onCancel: () => selection.setAllSelected(false),
                        }}
                    />
                </div>
                <div key='list' className="entities-list-body list-container">
                    <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                        <ClearFilterComponent
                            items={props.calendars}
                            filteredItems={applyFilter(props.calendars)}
                            onClearFilter={() => setActiveFilter(props.filters[0])}>
                            <EntityDetailsList
                                isFieldFake={isFieldFake}
                                getKey={getKey}
                                entities={applyFilter(props.calendars)}
                                entityType={EntityType.Calendar}
                                fields={props.fields.concat(fakeFields)}
                                displayFields={[nameof<ICalendarAttrs>("Name"), nameof<Calendar>("isDefault"), ...namesof<ICalendarAttrs>(["Status", "Description"])]}
                                selection={selection}
                                groups={buildListGroups()}
                                listGrouping={{
                                    getGroupKey: getCalendarGroupId,
                                    renderGroupHeader: (group, isCollapsed, isSelected, onToggleCollapse, onToggleSelect) => (
                                        <EntityGroupHeader
                                            group={group}
                                            isCollapsed={isCollapsed}
                                            isSelected={isSelected}
                                            onToggleCollapse={onToggleCollapse}
                                            onToggleSelect={group.key === USER_CALENDAR_GROUP_ID ? undefined : onToggleSelect}
                                            renderCounter={(grp) => <span className="subitems-counter">{grp.count}</span>}
                                        />
                                    ),
                                }}
                                onItemMenuRender={renderThreeDotsMenu}
                                {...updateCheckboxOptions({
                                    checkboxVisibility: canManage ? undefined : CheckboxVisibility.hidden
                                }, selectionModeCommands, true)}
                                fabricListProps={{
                                    constrainMode: ConstrainMode.unconstrained,
                                    onRenderDetailsHeader: onRenderDetailsHeader,
                                }}
                            />
                        </ClearFilterComponent>
                    </ScrollablePane>
                </div>
            </div>
        </div>
        {addNewCalendar &&
            <CalendarCreation
                onDismiss={() => setAddNewCalendar(false)}
            />
        }
        {!!toRemove.length &&
            <RemoveDialog
                onClose={() => setToRemove([])}
                onComplete={() => props.calendarsActions.removeCalendars(toRemove.map(_ => _.id))}
                dialogContentProps={buildRemoveDialogContentProps(toRemove)}
                confirmButtonProps={{ text: "Delete", disabled: toRemove.length === 0 }}
            >
            </RemoveDialog>
        }
        {props.deletionResult && (
            <RemoveDialog
                onClose={() => props.calendarsActions.dismissDeletionResult()}
                confirmButtonProps={{ text: "Got it" }}
                modalProps={{ styles: { main: { minWidth: 500 } } }}
                dialogContentProps={buildDeletionResultDialogContent(props.deletionResult)}
            >
                {props.deletionResult.length > 1 && props.deletionResult.some(_ => !_.isDeleted) && (
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline={true}>
                        Failed to delete the following calendars:
                        <ul>
                            {props.deletionResult.filter(_ => !_.isDeleted).map(_ => <li key={_.id}>{_.message}</li>)}
                        </ul>
                    </MessageBar>
                )}
            </RemoveDialog>
        )}
    </>;
};

const getKey = (calendar: Calendar): string => `${calendar.id}_${calendar.attributes.Status}`;

const buildRemoveDialogContentProps = (toRemove: Calendar[]): IDialogContentProps => {
    if (toRemove.length === 1) {
        return {
            title: "Delete calendar",
            subText: `Are you sure you want to delete calendar "${toRemove[0].attributes.Name}"?`
        }
    }

    return {
        title: "Delete calendars",
        subText: `Are you sure you want to delete selected calendars (${toRemove.length} items)?`
    }
}

const mapStateToProps = (state: ApplicationState): StateProps => {
    const fields = state.fields[EntityType.Calendar];
    const filters = FiltersStore.getFilter(state.filters, EntityType.Calendar);

    return {
        fields: fields.allIds.map(_ => fields.byId[_]),
        filters: filters.all,
        user: state.user,
        tenant: state.tenant,
        calendars: [
            ...state.calendars.allIds.map(_ => state.calendars.byId[_]),
            ...(isAdminViewer(state.user) ? [] : [getUserCalendar(state)])
        ],
        isLoading: state.calendars.isListLoading || state.calendars.isLoading,
        deletionResult: state.calendars.deletionResult,
    };
}

function mergeActionCreators(dispatch: any): ActionProps {
    return {
        calendarsActions: bindActionCreators(calendarActionCreators, dispatch),
        resourcesActions: bindActionCreators(ResourcesListStore.actionCreators, dispatch),
    };
}

export default connect(mapStateToProps, mergeActionCreators)(CalendarsList);

const buildStatusItemIfStatusDiffersAndNotDefault = (
    calendars: Calendar[],
    status: CalendarStatus,
    updateStatusMenuItem: IContextualMenuItem
): IContextualMenuItem | undefined =>
    calendars.some(_ => _.attributes.Status !== status && !_.isDefault) ? updateStatusMenuItem : undefined;


export const buildSetAsDefaultAction = (calendar: Calendar, makeCalendarDefault: (id: string) => void) =>
    !calendar.isDefault && calendar.isEditable && !calendar.isUserCalendar
        && calendar.attributes.Status === CalendarStatus.Active
        ? {
            key: "set-as-default",
            name: "Set as Default",
            disabled: calendar.attributes.Status !== CalendarStatus.Active,
            title: calendar.attributes.Status === CalendarStatus.Active
                ? "Calendar marked as Default will be pre-selected in the Project Calendar field during Project creation"
                : "Calendar is not active",
            iconProps: { iconName: "ActivateOrders" },
            onClick: () => makeCalendarDefault(calendar.id),
        }
        : undefined;
